/**
 * A calendar-only date control for the gradebook.
 *
 * WHY NOT <input type="date">. Two user-visible reasons. The native popup's
 * selected-day highlight is browser chrome - `accent-color` does not reach it
 * in Chromium - so it sat blue in a green app. And the text half of a native
 * date input accepts keystrokes, which is how a filter bound briefly read
 * year 0045 while being retyped. A button that only ever opens a calendar can
 * do neither: every value this emits is a day somebody clicked.
 *
 * The popover is position:fixed off the button's own rect (the sheet's status
 * menu uses the same trick), so it cannot be clipped by a scrolling table
 * cell or a modal's overflow, and it works identically in all three homes:
 * the range filter, the Assessments table, and the New Assessment modal.
 */

import { useEffect, useState } from "react";
import { CalendarDays, ChevronLeft, ChevronRight } from "lucide-react";
import { cn } from "@/lib/utils";
import { formatDateLong } from "@/gradebook/grading";

const MONTHS = [
  "January", "February", "March", "April", "May", "June",
  "July", "August", "September", "October", "November", "December",
];
/** Monday-first, matching the native picker teachers were used to. */
const DOW = ["M", "T", "W", "T", "F", "S", "S"];

const pad = (n) => String(n).padStart(2, "0");
const iso = (y, m, d) => `${y}-${pad(m + 1)}-${pad(d)}`;

/** Local today - `toISOString()` is UTC and rolls the date over mid-evening. */
function todayISO() {
  const d = new Date();
  return iso(d.getFullYear(), d.getMonth(), d.getDate());
}

function monthOf(value) {
  const [y, m] = String(value ?? todayISO()).split("-").map(Number);
  return { y, m: m - 1 };
}

export function DatePicker({ value, onChange, min, max, ariaLabel, buttonClassName }) {
  /* The popover's fixed anchor, or null while closed. */
  const [at, setAt] = useState(null);
  const [view, setView] = useState(() => monthOf(value));

  useEffect(() => {
    if (!at) return;
    const onKey = (e) => { if (e.key === "Escape") setAt(null); };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [at]);

  const open = (e) => {
    const r = e.currentTarget.getBoundingClientRect();
    setView(monthOf(value));
    setAt({
      left: Math.max(8, Math.min(r.left, window.innerWidth - 264)),
      top: Math.min(r.bottom + 4, window.innerHeight - 330),
    });
  };

  const { y, m } = view;
  const days = new Date(y, m + 1, 0).getDate();
  const lead = (new Date(y, m, 1).getDay() + 6) % 7;
  const blocked = (v) => (min && v < min) || (max && v > max);
  const step = (by) =>
    setView((prev) => {
      const next = new Date(prev.y, prev.m + by, 1);
      return { y: next.getFullYear(), m: next.getMonth() };
    });
  const today = todayISO();

  return (
    <>
      <button
        type="button"
        onClick={open}
        aria-label={ariaLabel}
        aria-haspopup="dialog"
        aria-expanded={!!at}
        className={cn("inline-flex items-center justify-between gap-2", buttonClassName)}
      >
        <span className="tabular-nums">{value ? formatDateLong(value) : "Pick a date"}</span>
        <CalendarDays size={14} className="shrink-0 opacity-60" />
      </button>

      {at && (
        <>
          {/* Backdrop over everything, including a modal, so the first click
              outside closes the calendar instead of acting on what is under
              it. z-[60] clears the modals' own z-50 overlays. */}
          <div className="fixed inset-0 z-[60]" onMouseDown={() => setAt(null)} />
          <div
            role="dialog"
            aria-label="Choose a date"
            className="fixed z-[61] w-[256px] rounded-lg border border-border bg-card p-3 shadow-card"
            style={{ left: at.left, top: at.top }}
          >
            <div className="mb-2 flex items-center justify-between">
              <span className="text-[13px] font-semibold text-foreground">
                {MONTHS[m]} {y}
              </span>
              <span className="flex items-center gap-1.5">
                <button
                  type="button"
                  onClick={() => step(-1)}
                  aria-label="Previous month"
                  className="flex h-7 w-7 items-center justify-center rounded-md border border-border bg-muted/60 text-foreground transition-colors hover:border-primary hover:bg-primary hover:text-primary-foreground"
                >
                  <ChevronLeft size={16} strokeWidth={2.5} />
                </button>
                <button
                  type="button"
                  onClick={() => step(1)}
                  aria-label="Next month"
                  className="flex h-7 w-7 items-center justify-center rounded-md border border-border bg-muted/60 text-foreground transition-colors hover:border-primary hover:bg-primary hover:text-primary-foreground"
                >
                  <ChevronRight size={16} strokeWidth={2.5} />
                </button>
              </span>
            </div>

            <div className="grid grid-cols-7 gap-y-0.5 text-center">
              {DOW.map((d, i) => (
                <span key={i} className="text-[10.5px] font-semibold uppercase text-muted-foreground">
                  {d}
                </span>
              ))}
              {Array.from({ length: lead }, (_, i) => <span key={`lead-${i}`} />)}
              {Array.from({ length: days }, (_, i) => {
                const d = i + 1;
                const v = iso(y, m, d);
                const off = blocked(v);
                return (
                  <button
                    key={d}
                    type="button"
                    disabled={off}
                    onClick={() => { onChange(v); setAt(null); }}
                    className={cn(
                      "mx-auto flex h-7 w-7 items-center justify-center rounded-md text-[12.5px] tabular-nums transition-colors",
                      v === value
                        ? "bg-primary font-semibold text-primary-foreground"
                        : off
                          ? "text-muted-foreground/30"
                          : "text-foreground hover:bg-primary/15",
                      v === today && v !== value && "font-semibold text-primary",
                    )}
                  >
                    {d}
                  </button>
                );
              })}
            </div>

            <div className="mt-2 flex justify-end border-t border-border pt-2">
              <button
                type="button"
                onClick={() => {
                  if (blocked(today)) setView(monthOf(today));
                  else { onChange(today); setAt(null); }
                }}
                className="text-[12px] font-medium text-primary hover:underline"
              >
                Today
              </button>
            </div>
          </div>
        </>
      )}
    </>
  );
}
